/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { forwardRef, useRef, useState, useEffect } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { useOutsideClick } from '@/core/hooks/useOutSideClick'
import Button from './header-button'
import { ArrowCircleDown, ArrowsLeftRight, ArrowCircleUp, Bank } from '@phosphor-icons/react'
import { useUserStore } from '@/store/auth/store'
import { useModalStore } from '@/store/modal/store'
import { SynapsStatus } from '@/store/auth/enum'
import { useTranslation } from 'react-i18next'
import DvxButton from '@/core/components/globals/header/dvx-button'
import { useQueryClient } from '@tanstack/react-query'
import { BinanceService } from '@/core/services/binanceService'
import { FEATURE_FLAGS } from '@/core/constants/feature-flags'

const TransactionButtons = forwardRef(() => {
  const { t } = useTranslation()
  const user = useUserStore((state) => state.user)
  const setModal = useModalStore((state) => state.setModal)
  const queryClient = useQueryClient()
  const buttonRefs = useRef<any[]>([])
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false)
  const [isMobile, setIsMobile] = useState<boolean>(false)
  const [isTablet, setIsTablet] = useState<boolean>(false)

  // Prefetch accounts data when user hovers over Transfer button
  const handlePrefetchTransfer = () => {
    queryClient.prefetchQuery({
      queryKey: ['allAccounts'],
      queryFn: () => BinanceService.getAllAccount(),
    });
  };

  // Handle deposit button click
  const handleOpenDepositModal = () => {
    if (user?.synapsStatus === SynapsStatus.APPROVED) {
      setModal({ deposit: true });
    } else {
      setModal({ kycRequired: true });
    }
    if (isMobile || isTablet) {
      setDropdownOpen(false);
    }
  };

  // Handle transfer button click
  const handleOpenTransferModal = () => {
    if (user?.synapsStatus === SynapsStatus.APPROVED) {
      setModal({ transfer: true });
    } else {
      setModal({ kycRequired: true });
    }
    if (isMobile || isTablet) {
      setDropdownOpen(false);
    }
  };

  // Handle withdraw button click
  const handleOpenWithdrawModal = () => {
    if (user?.synapsStatus === SynapsStatus.APPROVED) {
      setModal({ withdraw: true });
    } else {
      setModal({ kycRequired: true });
    }
    if (isMobile || isTablet) {
      setDropdownOpen(false);
    }
  };

  // Handle buy DVX button click
  const handleOpenBuyDvxModal = () => {
    if (user?.synapsStatus === SynapsStatus.APPROVED) {
      setModal({ buyDvx: true });
    } else {
      setModal({ kycRequired: true });
    }
    if (isMobile || isTablet) {
      setDropdownOpen(false);
    }
  };

  // Toggle dropdown
  const toggleDropdown = (e: any) => {
    e.preventDefault();
    setDropdownOpen((prev) => !prev);
  }

  // Close dropdown when clicking outside
  const refDropdown = useOutsideClick(() => {
    setDropdownOpen(false);
  })

  // Check device type based on screen width
  useEffect(() => {
    const checkDeviceType = () => {
      const width = window.innerWidth;
      setIsMobile(width < 480); // Mobile: less than 480px
      setIsTablet(width >= 480 && width < 768); // Tablet: between 480px and 768px
    }

    // Initial check
    checkDeviceType()

    // Add event listener for window resize
    window.addEventListener('resize', checkDeviceType)

    // Cleanup
    return () => {
      window.removeEventListener('resize', checkDeviceType)
    }
  }, [])

  // Desktop view - buttons displayed horizontally
  const DesktopButtons = () => (
    <div className="transaction-buttons__desktop">
      {FEATURE_FLAGS.DVX_ENABLED && (
        <Button
          open={handleOpenBuyDvxModal}
          modal={<></>}
          ref={(ref) => (buttonRefs.current[0] = ref)}>
          <>
            <DvxButton size={24} />
            <span className="button-text">DVX</span>
          </>
        </Button>
      )}
      <Button
        open={handleOpenDepositModal}
        modal={<></>}
        ref={(ref) => (buttonRefs.current[0] = ref)}>
        <>
          <ArrowCircleDown size={24} style={{ color: 'var(--text-primary)' }} />
          <span className="button-text">{t('common.deposit')}</span>
        </>
      </Button>
      <Button
        open={handleOpenTransferModal}
        modal={<></>}
        onMouseEnter={handlePrefetchTransfer}
        ref={(ref) => (buttonRefs.current[1] = ref)}>
        <>
          <ArrowsLeftRight size={24} style={{ color: 'var(--text-primary)' }} />
          <span className="button-text">{t('common.transfer')}</span>
        </>
      </Button>
      <Button
        open={handleOpenWithdrawModal}
        modal={<></>}
        ref={(ref) => (buttonRefs.current[2] = ref)}>
        <>
          <ArrowCircleUp size={24} style={{ color: 'var(--text-primary)' }} />
          <span className="button-text">{t('common.withdraw')}</span>
        </>
      </Button>
    </div>
  )

  // Tablet view - buttons with icons only
  const TabletButtons = () => (
    <div className="transaction-buttons__tablet">
      {FEATURE_FLAGS.DVX_ENABLED && (
        <Button
          open={handleOpenBuyDvxModal}
          modal={<></>}
          ref={(ref) => (buttonRefs.current[0] = ref)}>
          <>
            <DvxButton size={24} />
          </>
        </Button>
      )}
      <Button
        open={handleOpenDepositModal}
        modal={<></>}
        ref={(ref) => (buttonRefs.current[0] = ref)}>
        <>
          <ArrowCircleDown size={24} style={{ color: 'var(--text-primary)' }} />
        </>
      </Button>
      <Button
        open={handleOpenTransferModal}
        modal={<></>}
        onMouseEnter={handlePrefetchTransfer}
        ref={(ref) => (buttonRefs.current[1] = ref)}>
        <>
          <ArrowsLeftRight size={24} style={{ color: 'var(--text-primary)' }} />
        </>
      </Button>
      <Button
        open={handleOpenWithdrawModal}
        modal={<></>}
        ref={(ref) => (buttonRefs.current[2] = ref)}>
        <>
          <ArrowCircleUp size={24} style={{ color: 'var(--text-primary)' }} />
        </>
      </Button>
    </div>
  )

  // Mobile view - dropdown menu with icon only
  const MobileDropdown = () => (
    <div className="transaction-buttons__mobile" ref={refDropdown}>
      <div
        className={"transaction-buttons__mobile-button transaction-buttons__mobile-button--icon-only " + (dropdownOpen ? 'active' : '')}
        onClick={toggleDropdown}>
        <Bank size={24} style={{ color: 'var(--text-primary)' }} />
      </div>
      <AnimatePresence>
        {dropdownOpen && (
          <motion.div
            initial={{ opacity: 0, translateY: -20 }}
            animate={{ opacity: 1, translateY: 0 }}
            exit={{ opacity: 0, translateY: 0 }}
            transition={{
              type: 'spring',
              stiffness: 400,
              damping: 30,
              mass: 1,
              delay: 0,
            }}
            className="transaction-buttons__mobile-menu"
          >
            {FEATURE_FLAGS.DVX_ENABLED && (
              <a
                className="transaction-buttons__mobile-menu-item txt-button-m"
                onClick={handleOpenBuyDvxModal}
              >
                <span>
                  <DvxButton size={22} />
                </span>
                DVX
              </a>
            )}
            <a
              className="transaction-buttons__mobile-menu-item txt-button-m"
              onClick={handleOpenDepositModal}
            >
              <span>
                <ArrowCircleDown size={22} style={{ color: 'var(--text-primary)' }} weight={'light'} />
              </span>
              {t('common.deposit')}
            </a>
            <a
              className="transaction-buttons__mobile-menu-item txt-button-m"
              onClick={handleOpenTransferModal}
              onMouseEnter={handlePrefetchTransfer}
            >
              <span>
                <ArrowsLeftRight size={22} style={{ color: 'var(--text-primary)' }} weight={'light'} />
              </span>
              {t('common.transfer')}
            </a>
            <a
              className="transaction-buttons__mobile-menu-item txt-button-m"
              onClick={handleOpenWithdrawModal}
            >
              <span>
                <ArrowCircleUp size={22} style={{ color: 'var(--text-primary)' }} weight={'light'} />
              </span>
              {t('common.withdraw')}
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )

  return (
    <div className="transaction-buttons">
      {isMobile ? <MobileDropdown /> : isTablet ? <TabletButtons /> : <DesktopButtons />}
    </div>
  )
})

TransactionButtons.displayName = 'TransactionButtons'

export default TransactionButtons
